import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { fetchCurrentUser } from "../../redux/usersSlice";
import CustomLoader from "../Common/CustomLoader"; 

export const DashboardRoute = ({ children }) => {
  const dispatch = useDispatch();
  const { token, currentUser, loading, error } = useSelector(
    (state) => state.users
  );


  useEffect(() => {
    if (token && !currentUser) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, token, currentUser]);

  // Handle token expiration error
  useEffect(() => {
    if (error && error.message === "Token expired") {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  }, [error]);

  // No token => redirect
  if (!token) {
    return <Navigate to="/" replace />;
  }

  // Still fetching user => show loader
  if (loading && !currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
        <CustomLoader /> {/* Replaced CircularProgress with CustomLoader */}
      </Box>
    );
  }

  // Done fetching but user is still null => treat as not authenticated
  if (!currentUser) {
    return <Navigate to="/acceuil" replace />;
  }

  // Now it's safe to read currentUser.status
  if (currentUser.status === "creation" || currentUser.status === "chgPwd") {
    return <Navigate to="/2fa" replace />;
  }

  if (currentUser.status !== "in use") {
    return <Navigate to="/interdit" replace />;
  }

  return children; // Or the actual protected component
};

/**
 * TwoFactorAuthRoute protects the /2fa route.
 * - If no token exists, it redirects to the landing page ("/").
 * - While waiting for the user fetch to complete, it shows a spinner.
 * - If fetch is complete but currentUser is null, it shows a spinner.
 * - If the user is fully authenticated (status "in use"), it redirects to /acceuil.
 * - If the user is in 2FA-required states ("creation" or "chgPwd"), it renders the children.
 * - Otherwise, it redirects to /interdit.
 */
export const TwoFactorAuthRoute = ({ children }) => {
  const dispatch = useDispatch();
  const { token, currentUser, loading, error } = useSelector(
    (state) => state.users
  );

  // Fetch the user if we have a token but currentUser is null
  useEffect(() => {
    if (token && !currentUser) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, token, currentUser]);

  // Handle token expiration error
  useEffect(() => {
    if (error && error.message === "Token expired") {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  }, [error]);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  // Show spinner while loading user for the first time
  if (loading && !currentUser) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
        <CustomLoader /> {/* Replaced CircularProgress with CustomLoader */}
      </Box>
    );
  }

  // If user data has been fetched and currentUser is still null => unauth
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Safe to check currentUser.status now:
  if (currentUser.status === "in use") {
    return <Navigate to="/acceuil" replace />;
  }

  if (currentUser.status === "creation" || currentUser.status === "chgPwd") {
    return children; // Render the 2FA page
  }

  return <Navigate to="/interdit" replace />;
};
